import {
  WorkbenchGalleryCard,
  type WorkbenchGalleryCardProps,
} from './WorkbenchGalleryCard';
import {
  type WorkbenchMasonryItemSize,
} from './WorkbenchMasonryLayout';
import { WorkbenchCollection } from './WorkbenchCollection';
import {
  getDefaultGalleryDisplayText,
  getDefaultGalleryFieldValue,
  isDefaultGalleryAudioField,
  isDefaultGalleryImageField,
  isDefaultGalleryMediaColumn,
  isDefaultGallerySummaryCandidate,
  isDefaultGalleryTitleCandidate,
  type WorkbenchGalleryColumn,
} from './workbenchGalleryUtils';

const GALLERY_GRID_GAP_PX = 12;

export interface WorkbenchGalleryViewProps<Row = any>
  extends Omit<WorkbenchGalleryCardProps<Row>, 'row' | 'columns' | 'onClick' | 'isSelected' | 'isMasonry'> {
  rows?: Row[];
  columns?: WorkbenchGalleryColumn[];
  onRowClick?: (row: Row, rowIndex: number) => void;
  selectedLinkKey?: string;
  selectedRowKey?: string;
  className?: string;
  isMasonry?: boolean;
  galleryLayoutMode?: 'auto' | 'rail' | string;
  galleryRailCardWidth?: number;
  galleryGridColumns?: number | 'auto' | string;
  galleryGridCardWidth?: number | string;
  galleryMasonryColumnWidth?: number | string;
  masonryPlacementStrategy?: 'shelf' | 'dense';
  getMasonryItemSize?: (
    row: Row,
    context: {
      defaultSize: WorkbenchMasonryItemSize;
      columnCount: number;
      columnWidth: number;
      containerWidth: number;
      gap: number;
      placementStrategy: 'shelf' | 'dense';
      rowHeight: number;
    },
  ) => WorkbenchMasonryItemSize | undefined;
}

function getNormalizedGalleryGridCardWidth(cardWidth: unknown) {
  if (typeof cardWidth === 'number' && Number.isFinite(cardWidth) && cardWidth > 0) {
    return `${cardWidth}px`;
  }

  if (typeof cardWidth === 'string' && cardWidth.trim()) {
    return cardWidth.trim();
  }

  return undefined;
}

function getDefaultGalleryDetailLimit(density?: string) {
  if (density === 'compact') return 2;
  if (density === 'detailed') return 6;
  return 4;
}

function getNormalizedGalleryBentoColumns(columns: unknown, density?: string) {
  if (columns === 1 || columns === 2 || columns === 3) return Number(columns);
  return density === 'detailed' ? 1 : 2;
}

function getNormalizedGalleryDetailLimit({
  density,
  visibility,
  limit,
}: {
  density?: string;
  visibility?: string;
  limit?: number | string;
}) {
  if (visibility === 'all') return Number.POSITIVE_INFINITY;
  const parsedLimit = Number.parseInt(String(limit), 10);
  if (Number.isFinite(parsedLimit) && parsedLimit > 0) return parsedLimit;
  return getDefaultGalleryDetailLimit(density);
}

function getGalleryCardWidthMetrics(density?: string) {
  if (density === 'compact') return { min: 220, ideal: 244, max: 268 };
  if (density === 'detailed') return { min: 320, ideal: 372, max: 420 };
  return { min: 260, ideal: 304, max: 348 };
}

function getNormalizedGalleryGridColumns(columns: unknown) {
  if (columns === undefined || columns === null || columns === '' || columns === 'auto') {
    return undefined;
  }

  const parsedColumns = Number.parseInt(String(columns), 10);
  if (!Number.isFinite(parsedColumns) || parsedColumns <= 0) return undefined;
  return Math.max(1, Math.min(parsedColumns, 8));
}

function getGalleryTextLength(value: unknown) {
  if (value === null || value === undefined) return 0;
  const text = String(value).trim();
  return text && text !== '—' ? text.length : 0;
}

function getGalleryFieldText<Row>({
  column,
  row,
  getFieldValue,
  getDisplayText,
}: {
  column?: WorkbenchGalleryColumn | null;
  row: Row;
  getFieldValue: (column: WorkbenchGalleryColumn, row: Row) => unknown;
  getDisplayText: (value: unknown) => unknown;
}) {
  if (!column) return '';
  const rawValue = getFieldValue(column, row);
  if (rawValue === null || rawValue === undefined || rawValue === '') return '';
  const textValue = getDisplayText(rawValue);
  return textValue === '—' ? '' : String(textValue);
}

function estimateLineCount({
  text,
  charsPerLine,
  maxLines,
}: {
  text: unknown;
  charsPerLine: number;
  maxLines: number;
}) {
  const textLength = getGalleryTextLength(text);
  if (!textLength) return 0;
  return Math.max(1, Math.min(maxLines, Math.ceil(textLength / charsPerLine)));
}

function estimateGalleryDetailCellHeight({
  text,
  density,
  bentoColumns,
}: {
  text: unknown;
  density?: string;
  bentoColumns: number;
}) {
  const baseHeight = density === 'detailed' ? 54 : density === 'compact' ? 44 : 48;
  const charsPerLine = bentoColumns === 1
    ? (density === 'detailed' ? 32 : 26)
    : (density === 'detailed' ? 18 : 15);
  const maxLines = density === 'detailed' ? 3 : 2;
  const lineCount = estimateLineCount({ text, charsPerLine, maxLines });

  if (!lineCount) return baseHeight;
  return baseHeight + Math.max(0, lineCount - 1) * 16;
}

function estimateGalleryDetailGridHeight({
  columns,
  density,
  bentoColumns,
}: {
  columns: Array<{ text: string }>;
  density?: string;
  bentoColumns: number;
}) {
  if (!columns.length) return 0;

  const cellHeights = columns.map((column) => estimateGalleryDetailCellHeight({
    text: column.text,
    density,
    bentoColumns,
  }));

  if (bentoColumns === 1) {
    return 12 + cellHeights.reduce((sum, height) => sum + height, 0) + Math.max(0, columns.length - 1) * 8;
  }

  let totalHeight = 12;

  for (let index = 0; index < cellHeights.length; index += bentoColumns) {
    const rowHeights = cellHeights.slice(index, index + bentoColumns);
    totalHeight += Math.max(...rowHeights);
    if (index + bentoColumns < cellHeights.length) totalHeight += 8;
  }

  return totalHeight;
}

function getDefaultGalleryMasonryItemSize<Row>({
  row,
  columns,
  galleryFieldKey,
  galleryDensity,
  galleryBentoColumns,
  galleryDetailVisibility,
  galleryDetailLimit,
  getFieldValue,
  isMediaColumn,
  isTitleCandidate,
  isSummaryCandidate,
  getDisplayText,
  isImageField,
  isAudioField,
  cardVariant,
  renderPreview,
  renderLeadingVisual,
  hiddenDetailFieldKeys,
  layout,
  resolveRecordVisual,
}: {
  row: Row;
  columns: WorkbenchGalleryColumn[];
  galleryFieldKey?: string;
  galleryDensity?: string;
  galleryBentoColumns?: number;
  galleryDetailVisibility?: string;
  galleryDetailLimit?: number | string;
  getFieldValue: (column: WorkbenchGalleryColumn, row: Row) => unknown;
  isMediaColumn: (column: WorkbenchGalleryColumn, row: Row) => boolean;
  isTitleCandidate: (column: WorkbenchGalleryColumn, row: Row) => boolean;
  isSummaryCandidate: (column: WorkbenchGalleryColumn, row: Row) => boolean;
  getDisplayText: (value: unknown) => unknown;
  isImageField: (column: WorkbenchGalleryColumn, value: unknown) => boolean;
  isAudioField: (column: WorkbenchGalleryColumn, value: unknown) => boolean;
  cardVariant?: string;
  renderPreview?: unknown;
  renderLeadingVisual?: unknown;
  hiddenDetailFieldKeys?: string[];
  layout?: { columnWidth?: number };
  resolveRecordVisual?: WorkbenchGalleryCardProps<Row>['resolveRecordVisual'];
}) {
  const iconColumn = columns.find((column) => column.renderType === 'icon');
  const iconVisual = iconColumn && resolveRecordVisual ? resolveRecordVisual((row as any)?.[iconColumn.key], row, iconColumn) : null;
  const selectedGalleryColumn = galleryFieldKey !== 'auto'
    ? columns.find((column) => column.key === galleryFieldKey && isMediaColumn(column, row))
    : null;
  const mediaColumn = selectedGalleryColumn || columns.find((column) => isMediaColumn(column, row)) || null;
  const mediaValue = mediaColumn ? getFieldValue(mediaColumn, row) : null;
  const isImageMedia = mediaColumn ? isImageField(mediaColumn, mediaValue) : false;
  const isAudioMedia = mediaColumn ? isAudioField(mediaColumn, mediaValue) : false;
  const hasDefaultLeadingVisual = Boolean(iconVisual);
  const hasLeadingVisual = Boolean(renderLeadingVisual) || hasDefaultLeadingVisual;
  const contentColumns = columns.filter((column) => column.key !== mediaColumn?.key && column.renderType !== 'icon');
  const titleCandidates = contentColumns.filter((column) => {
    if (!column || isMediaColumn(column, row) || !isTitleCandidate(column, row)) return false;
    const value = getFieldValue(column, row);
    return !(value === null || value === undefined || value === '');
  });
  const titleColumn = titleCandidates[0] || null;
  const titleText = titleColumn
    ? getGalleryFieldText({ column: titleColumn, row, getFieldValue, getDisplayText })
    : String(getDisplayText((row as any)?.linkKey || (row as any)?.id || ''));
  const summaryColumn = contentColumns.find((column) => {
    if (!column || column.key === titleColumn?.key || isMediaColumn(column, row) || !isSummaryCandidate(column, row)) return false;
    const summaryText = getGalleryFieldText({ column, row, getFieldValue, getDisplayText });
    return summaryText.length > 12;
  }) || contentColumns.find((column) => {
    if (!column || column.key === titleColumn?.key || isMediaColumn(column, row) || !isSummaryCandidate(column, row)) return false;
    return getGalleryTextLength(getGalleryFieldText({ column, row, getFieldValue, getDisplayText })) > 0;
  }) || null;
  const summaryText = summaryColumn ? getGalleryFieldText({ column: summaryColumn, row, getFieldValue, getDisplayText }) : '';
  const hiddenDetailKeySet = new Set(hiddenDetailFieldKeys);
  const detailColumns = contentColumns
    .filter((column) => column.key !== titleColumn?.key && column.key !== summaryColumn?.key && !hiddenDetailKeySet.has(column.key))
    .map((column) => ({
      column,
      text: getGalleryFieldText({ column, row, getFieldValue, getDisplayText }),
    }));
  const resolvedDetailLimit = getNormalizedGalleryDetailLimit({
    density: galleryDensity,
    visibility: galleryDetailVisibility,
    limit: galleryDetailLimit,
  });
  const visibleDetailColumns = Number.isFinite(resolvedDetailLimit)
    ? detailColumns.slice(0, resolvedDetailLimit)
    : detailColumns;
  const hiddenDetailCount = Math.max(detailColumns.length - visibleDetailColumns.length, 0);
  const resolvedBentoColumns = getNormalizedGalleryBentoColumns(galleryBentoColumns, galleryDensity);
  const resolvedColumnWidth = layout?.columnWidth || getGalleryCardWidthMetrics(galleryDensity).ideal;

  if (cardVariant === 'image-only') {
    return {
      width: resolvedColumnWidth,
      height: Math.round(resolvedColumnWidth * 1.25),
    };
  }

  if (cardVariant === 'preview-only') {
    return {
      width: resolvedColumnWidth,
      height: Math.round(resolvedColumnWidth * 0.82),
    };
  }

  let estimatedHeight = galleryDensity === 'detailed' ? 84 : galleryDensity === 'compact' ? 64 : 72;

  if (typeof renderPreview === 'function') {
    estimatedHeight += galleryDensity === 'detailed' ? 188 : galleryDensity === 'compact' ? 140 : 164;
  } else if (isImageMedia) {
    estimatedHeight += Math.round(resolvedColumnWidth * (galleryDensity === 'detailed' ? 0.72 : 0.62)) + 12;
  } else if (isAudioMedia) {
    estimatedHeight += 92;
  }

  const titleLines = estimateLineCount({
    text: titleText,
    charsPerLine: galleryDensity === 'detailed' ? 26 : 24,
    maxLines: 2,
  }) || 1;
  const summaryLines = estimateLineCount({
    text: summaryText,
    charsPerLine: hasLeadingVisual ? 22 : (galleryDensity === 'detailed' ? 30 : 26),
    maxLines: galleryDensity === 'detailed' ? 4 : 2,
  });
  const textHeaderHeight = 26 + titleLines * 18 + (summaryLines > 0 ? summaryLines * 16 + 4 : 0);
  estimatedHeight += Math.max(hasLeadingVisual ? 56 : 0, textHeaderHeight);
  estimatedHeight += estimateGalleryDetailGridHeight({
    columns: visibleDetailColumns,
    density: galleryDensity,
    bentoColumns: resolvedBentoColumns,
  });

  if (hiddenDetailCount > 0) estimatedHeight += 18;
  if (!summaryText && visibleDetailColumns.length === 0) estimatedHeight += 24;

  return {
    width: resolvedColumnWidth,
    height: estimatedHeight,
  };
}

export function WorkbenchGalleryView<Row = any>({
  rows = [],
  columns = [],
  onRowClick,
  cardInteraction = 'button',
  selectedLinkKey,
  selectedRowKey,
  className,
  isMasonry = false,
  galleryLayoutMode = 'auto',
  galleryRailCardWidth,
  galleryGridColumns,
  galleryGridCardWidth,
  galleryMasonryColumnWidth,
  masonryPlacementStrategy = 'shelf',
  galleryFieldKey = 'auto',
  galleryDensity = 'comfortable',
  galleryBentoColumns = 2,
  galleryDetailVisibility = 'limit',
  galleryDetailLimit,
  shouldEnhanceMultimodal = true,
  getFieldValue,
  isMediaColumn,
  isTitleCandidate,
  isSummaryCandidate,
  getDisplayText,
  renderFieldValue,
  getImagePreviewSrc,
  isImageField,
  isAudioField,
  AudioPlayerComponent,
  cardVariant = 'default',
  galleryDetailPresentation,
  renderPreview,
  renderMeta,
  renderLeadingVisual,
  resolveRecordVisual,
  hiddenDetailFieldKeys = [],
  labels,
  formatLabel,
  getMasonryItemSize,
}: WorkbenchGalleryViewProps<Row>) {
  const { min: resolvedMinCardWidth, ideal: resolvedIdealCardWidth, max: resolvedMaxCardWidth } = getGalleryCardWidthMetrics(galleryDensity);
  const resolvedRailCardWidth = galleryRailCardWidth || resolvedMinCardWidth;
  const resolvedGridColumnCount = getNormalizedGalleryGridColumns(galleryGridColumns);
  const resolvedGridCardWidth = getNormalizedGalleryGridCardWidth(galleryGridCardWidth);
  const resolvedMasonryColumnWidth = galleryMasonryColumnWidth || resolvedMinCardWidth;
  const isRailLayout = galleryLayoutMode === 'rail';
  const resolvedGetFieldValue = getFieldValue || getDefaultGalleryFieldValue;
  const resolvedGetDisplayText = getDisplayText || getDefaultGalleryDisplayText;
  const resolvedIsImageField = isImageField || isDefaultGalleryImageField;
  const resolvedIsAudioField = isAudioField || isDefaultGalleryAudioField;
  const resolvedIsMediaColumn = isMediaColumn || ((column: WorkbenchGalleryColumn, row: Row) => (
    isDefaultGalleryMediaColumn(column, row, {
      getFieldValue: resolvedGetFieldValue as any,
      shouldEnhanceMultimodal,
      isImageField: resolvedIsImageField,
      isAudioField: resolvedIsAudioField,
    })
  ));
  const resolvedIsTitleCandidate = isTitleCandidate || ((column: WorkbenchGalleryColumn, row: Row) => (
    isDefaultGalleryTitleCandidate(column, row, { isMediaColumn: resolvedIsMediaColumn as any })
  ));
  const resolvedIsSummaryCandidate = isSummaryCandidate || ((column: WorkbenchGalleryColumn, row: Row) => (
    isDefaultGallerySummaryCandidate(column, row, { isMediaColumn: resolvedIsMediaColumn as any })
  ));
  const resolvedRenderFieldValue = renderFieldValue || ((column: WorkbenchGalleryColumn, row: Row, { compact } = {}) => (
    <span>{resolvedGetDisplayText(resolvedGetFieldValue(column, row))}</span>
  ));

  const renderGalleryCard = (row: Row, rowIndex: number) => (
    <WorkbenchGalleryCard
      key={(row as any)?.id || (row as any)?.linkKey || rowIndex}
      row={row}
      columns={columns}
      cardInteraction={cardInteraction}
      onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
      isSelected={String((row as any)?.linkKey || (row as any)?.id || '') === String(selectedRowKey || selectedLinkKey || '')}
      isMasonry={isMasonry}
      galleryFieldKey={galleryFieldKey}
      galleryDensity={galleryDensity}
      galleryBentoColumns={galleryBentoColumns}
      galleryDetailVisibility={galleryDetailVisibility}
      galleryDetailLimit={galleryDetailLimit}
      galleryDetailPresentation={galleryDetailPresentation}
      shouldEnhanceMultimodal={shouldEnhanceMultimodal}
      getFieldValue={resolvedGetFieldValue as any}
      isMediaColumn={resolvedIsMediaColumn as any}
      isTitleCandidate={resolvedIsTitleCandidate as any}
      isSummaryCandidate={resolvedIsSummaryCandidate as any}
      getDisplayText={resolvedGetDisplayText}
      renderFieldValue={resolvedRenderFieldValue as any}
      getImagePreviewSrc={getImagePreviewSrc}
      isImageField={resolvedIsImageField}
      isAudioField={resolvedIsAudioField}
      AudioPlayerComponent={AudioPlayerComponent}
      cardVariant={cardVariant}
      renderPreview={renderPreview}
      renderMeta={renderMeta}
      renderLeadingVisual={renderLeadingVisual}
      resolveRecordVisual={resolveRecordVisual}
      hiddenDetailFieldKeys={hiddenDetailFieldKeys}
      labels={labels}
      formatLabel={formatLabel}
    />
  );

  const collectionMode = isMasonry
    ? masonryPlacementStrategy === 'dense' ? 'dense' : 'masonry'
    : isRailLayout ? 'rail' : 'grid';

  return (
    <WorkbenchCollection
      items={rows}
      classNames={{ layout: className }}
      config={{
        getItemKey: (row: Row, rowIndex: number) => (row as any)?.id || (row as any)?.linkKey || rowIndex,
        layout: {
          mode: collectionMode,
          gap: GALLERY_GRID_GAP_PX,
          columns: isMasonry
            ? 'auto'
            : resolvedGridColumnCount
              ? {
                  mobile: 1,
                  tablet: Math.min(2, resolvedGridColumnCount),
                  desktop: resolvedGridColumnCount,
                }
              : 'auto',
          cardWidth: isRailLayout ? resolvedRailCardWidth : undefined,
          minCardWidth: resolvedGridCardWidth ?? resolvedMinCardWidth,
          idealCardWidth: isMasonry
            ? resolvedMasonryColumnWidth
            : resolvedGridCardWidth ?? resolvedIdealCardWidth,
          maxCardWidth: resolvedGridCardWidth ?? resolvedMaxCardWidth,
          placementStrategy: masonryPlacementStrategy,
          getItemSize: isMasonry
            ? (row: Row, layout) => {
              const defaultSize = getDefaultGalleryMasonryItemSize({
                row,
                columns,
                galleryFieldKey,
                galleryDensity,
                galleryBentoColumns,
                galleryDetailVisibility,
                galleryDetailLimit,
                getFieldValue: resolvedGetFieldValue as any,
                isMediaColumn: resolvedIsMediaColumn as any,
                isTitleCandidate: resolvedIsTitleCandidate as any,
                isSummaryCandidate: resolvedIsSummaryCandidate as any,
                getDisplayText: resolvedGetDisplayText,
                isImageField: resolvedIsImageField,
                isAudioField: resolvedIsAudioField,
                cardVariant,
                renderPreview,
                renderLeadingVisual,
                hiddenDetailFieldKeys,
                layout,
                resolveRecordVisual,
              });

              if (typeof getMasonryItemSize !== 'function') return defaultSize;
              const customSize = getMasonryItemSize(row, {
                ...layout,
                placementStrategy: layout.placementStrategy === 'dense' ? 'dense' : 'shelf',
                defaultSize,
              }) || {};
              return {
                ...defaultSize,
                ...customSize,
              };
            }
            : undefined,
        },
      }}
      renderItem={(row, context) => renderGalleryCard(row, context.index)}
    />
  );
}
